import React, { useState, useRef, useCallback } from 'react';
import { ImageHistoryItem, EditState } from './types';
import { geminiService } from './services/geminiService';
import { SAMPLE_PROMPTS, CATEGORIZED_PROMPTS, Icons } from './constants';
import Button from './components/Button';

const WATERMARK_PNG_BASE64 = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAACXBIWXMAAAsTAAALEwEAmpwYAAAFJUlEQVR4nO2dW28VVRTHf8PBUqWl1BYpBVEeIJiAGpQHQY2GByEa9UHoR4gm+mKiD9EnffD9DP6APogxXmIUhHAF0URAUAmCEpECSkulULmUpffpYVvO5pzpM2vPnpnD7P+TzE7Pvmdm1l7rM7vXmhkVDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDOyC27XfW0XvAnmS3id5lPRe0rtJ3id9nPQU6fPkf6H3N5BfoPcByW+T3yS9SnqZ5ALpFdKXSX9D+hLpc6S/IX2G9GfS39D7M71P0vuY5FHS+0ifIP0N6U9If0T6A9KXSP9G+jP9P6U3R/9P6O8kv0n+K+mD9P6A9BnpU9I7SD8i/RPpV0hfo3yB8ivkr6IskX5A+X3S+6T3SL9N+mXSy6QXSK+Q/oL0BdInSf9C+gzp06S/ovdnep+kN0t6m/S/pf8f+n+gf4X+Bfpv6F+gv0Z/jf4S/SX6C/Tn6M/RH6M/Rn+E/hD9Afo99Hvo99DvoN9Gv41+C/0m+k30G+g30K+jX0O/gn4F/Qr6ZfRL6BfQL6BfQL+Afh79PPp59PPox9GPoR9DP4Z+CP0Q+iH0A+iH0A+gX0C/gH4e/Sz6WfSz6GfRz6CfQT+DfgL9BPoJ9BPof6D/Af8O/jt8Y8zE8o0RE8szRkwEzxgxLnzGiDHhM0ZMGp8xYsz4jBGjxmeMGBM+Y8QE8RkjxorPGDFGfMaIEeMzRowTnzFinPiMEWPBZ4wYKz5jxGjxGSPGis8YMUp8xohR4zNGjA6fMWKE+IwRI8VnjBgtPmPE+PAZI0aNzxjRN3zGiFHjM0b0DZ8xYpT4jBEjxGeMGBU+Y8So8BkjBofPGDEmfMaICeMzRowdnzFiTPiMEePFZ4wYKz5jxHjxGSPGis8YMUp8xohR4zNGjA6fMWKE+IwRI8VnjBgtPmPE+PAZI0aNzxjRN3zGiFHjM0b0DZ8xYsz4jBFjxGeMGBU+Y8So8BkjBofPGDEmfMaICeMzRowdnzFiTPiMEePFZ4wYKz5jxHjxGSPGis8YMUp8xohR4zNGjA6fMWKE+IwRI8VnjBgtPmPE+PAZI0aNzxjRN3zGiFHjM0b0DZ8xYmT4jBEjxGeMmCA+Y8TY8RkjxoTPGDFefMaIseIzRowWnzFirPiMEaPEZ4wYNT5jxOjwGSNGiM8YMVKX/m9VvQPkOfoV9MvomvIlyufIn6Gv6K/RX6G/pP9P9Ffon6M/S/8b/VP0p+iP0h+mP0S/l34X/Rb6DfSb6NfSr6Gv6VfQL6NfQr+EfgH9fPqZ9DPpZ9DPoJ9Av4B+Hv08+nn0s+hn0c+gn0E/gX4C/QT6CfQT6H/Ab8u/N6Zj58aoiesbYye+b0yf+74xfX74xvS57xvT54Z3jJ8bvjF+7vjG+LnzG+PnzW+Mn7v8Cfq99HuYfiv9Dvpt5pvol5lvoF9mvon+Hfq+6H+mf4L+Kfqn6J+if4X+Ofo36L+lf47+Ffo36L+h/4n+FfoX6B+if4H+OfqX6J+hf4n+Ffp+6H+iP0B/mP4w/V76n/U+6302+y30m+hfov8N+p8YGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBiYiL8A9jLg7004RjIAAAAASUVORK5CYII=`;

const App: React.FC = () => {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [history, setHistory] = useState<ImageHistoryItem[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({
    isEditing: false,
    error: null,
    progressMessage: '',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const applyWatermark = useCallback(async (base64Image: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(base64Image); return; }
        ctx.drawImage(img, 0, 0);
        const logo = new Image();
        logo.crossOrigin = "anonymous";
        logo.onload = () => {
          const logoSize = Math.min(img.width, img.height) * 0.12;
          const padding = Math.min(img.width, img.height) * 0.03; 
          const x = img.width - logoSize - padding;
          const y = img.height - logoSize - padding;
          ctx.save();
          ctx.globalAlpha = 0.55; 
          ctx.drawImage(logo, x, y, logoSize, logoSize);
          ctx.restore();
          resolve(canvas.toDataURL('image/png'));
        };
        logo.onerror = () => resolve(base64Image);
        logo.src = WATERMARK_PNG_BASE64;
      };
      img.onerror = () => resolve(base64Image);
      img.src = base64Image;
    });
  }, []);

  const handleGenerate = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!prompt.trim() || editState.isEditing) return;

    setEditState({ isEditing: true, error: null, progressMessage: 'Synthesizing Vision...' });

    try {
      const { imageUrl, textResponse } = await geminiService.editImage(currentImage, prompt);

      if (imageUrl) {
        const watermarkedUrl = await applyWatermark(imageUrl);
        const newItem: ImageHistoryItem = {
          id: Date.now().toString(),
          url: watermarkedUrl,
          prompt: prompt,
          timestamp: Date.now(),
        };
        setHistory(prev => [newItem, ...prev]);
        setCurrentImage(watermarkedUrl);
        if (!originalImage) setOriginalImage(watermarkedUrl);
        setPrompt('');
      } else if (textResponse) {
         setEditState(prev => ({ ...prev, error: textResponse }));
      }
    } catch (err: any) {
      setEditState(prev => ({ ...prev, error: err.message || "Stay Tuned — We’re Building This for You!" }));
    } finally {
      setEditState(prev => ({ ...prev, isEditing: false }));
    }
  };

  const deleteHistoryItem = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setHistory(prev => prev.filter(item => item.id !== id));
  };

  const clearAllHistory = () => {
    setHistory([]);
  };

  const downloadImage = (imgUrl: string) => {
    if (!imgUrl) return;
    const link = document.createElement('a');
    link.href = imgUrl;
    const ext = imgUrl.split(';')[0].split('/')[1] || 'png';
    link.download = `BabalVision-${Date.now()}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen flex flex-col max-w-[1800px] mx-auto px-4 md:px-12 py-4 transition-all duration-700 overflow-x-hidden">
      <nav className="flex flex-col items-start gap-1 mb-4 px-2">
        <h1 className="text-3xl md:text-6xl font-black animate-title-dynamic tracking-tighter">
          BabalVision AI
        </h1>
        <p className="text-white/50 text-[10px] uppercase tracking-[0.5em] mt-3 font-bold">
          High-Fidelity Neural Vision
        </p>
      </nav>

      <main className="flex-1 flex flex-col items-center justify-center gap-6 w-full max-w-[1750px] mx-auto">
        <div className="w-full relative group">
          <div className={`relative flex items-center justify-center transition-all duration-500 ease-in-out bg-[#050505] rounded-[2.5rem] md:rounded-[4rem] border border-white/5 shadow-[0_0_150px_rgba(0,0,0,0.9)] overflow-hidden ${
            !currentImage && !editState.isEditing ? 'h-0 opacity-0' : 'min-h-[400px] md:h-[75vh] w-full'
          }`}>
            
            {editState.isEditing ? (
              <div className="loading-gif-overlay">
                <div className="loading-gif-circle scale-75 md:scale-100">
                  <img src="https://i.pinimg.com/originals/4d/79/e4/4d79e45299ba276f530cbda84f5eca05.gif" alt="Neural active..." className="loading-gif" />
                </div>
              </div>
            ) : currentImage ? (
              <img src={currentImage} alt="Neural Output" className="w-full h-full object-contain p-2 cursor-pointer transition-transform duration-700 hover:scale-[1.002]" onClick={() => setIsPreviewOpen(true)} />
            ) : null}

            {currentImage && !editState.isEditing && (
              <div className="absolute top-4 right-4 md:top-8 md:right-8 flex gap-2 md:gap-4 opacity-0 group-hover:opacity-100 transition-all z-10">
                <Button variant="ghost" className="bg-black/60 backdrop-blur-3xl border border-white/10 rounded-full p-3 md:p-4" onClick={() => setCurrentImage(originalImage)} icon={<Icons.Refresh className="w-5 h-5 md:w-6 md:h-6" />} />
                <Button variant="action" className="rounded-full p-3 md:p-4" onClick={() => downloadImage(currentImage)} icon={<Icons.Download className="w-5 h-5 md:w-6 md:h-6" />} />
              </div>
            )}
          </div>
        </div>

        <div className="w-full max-w-4xl space-y-4 md:space-y-6">
          {currentImage && (
            <div className="flex flex-wrap gap-2 justify-center">
              {Object.keys(CATEGORIZED_PROMPTS).map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                  className={`px-3 md:px-4 py-1.5 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all border ${
                    activeCategory === cat ? 'bg-white text-black border-white' : 'bg-white/5 text-slate-400 border-white/10 hover:border-white/30'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {activeCategory && currentImage && (
            <div className="flex flex-wrap gap-2 justify-center animate-in fade-in slide-in-from-top-2">
              {CATEGORIZED_PROMPTS[activeCategory as keyof typeof CATEGORIZED_PROMPTS].map(p => (
                <button
                  key={p}
                  onClick={() => setPrompt(p)}
                  className="px-3 py-1 rounded-lg text-[9px] font-bold bg-white/5 border border-white/5 text-slate-300 hover:bg-white/10 hover:text-white transition-all"
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          <div className="chat-input-container p-[1px]">
            <div className="relative flex flex-col min-h-[80px] md:min-h-[90px] w-full bg-[#0a0a0a] rounded-[2rem] md:rounded-[2.5rem] z-10 overflow-hidden">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Initiate vision command..."
                  className="w-full bg-transparent text-white text-base md:text-lg font-medium outline-none resize-none px-6 pt-4 pb-8 placeholder:text-slate-800 leading-tight"
                  disabled={editState.isEditing}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                />
                
                <div className="flex items-center justify-between mt-auto px-4 pb-3">
                  <div className="flex items-center gap-3 md:gap-4">
                    <button onClick={() => fileInputRef.current?.click()} className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all active:scale-90" title="Link Media" disabled={editState.isEditing}>
                      <Icons.Plus className="w-5 h-5 md:w-6 md:h-6" />
                    </button>
                    {currentImage && (
                      <div className="flex items-center gap-2 px-2.5 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                        <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest text-emerald-200">Edit Active</span>
                        <button onClick={() => {setOriginalImage(null); setCurrentImage(null);}} className="ml-1 text-slate-500 hover:text-rose-500">✕</button>
                      </div>
                    )}
                  </div>

                  <Button variant="action" onClick={handleGenerate} isLoading={editState.isEditing} disabled={!prompt.trim() || editState.isEditing} className="px-6 md:px-10 py-2 rounded-xl md:rounded-2xl">
                    <span className="uppercase tracking-widest font-black text-[10px] md:text-xs">GENERATE</span>
                  </Button>
                </div>
            </div>
          </div>

          {currentImage && (
            <div className="overflow-hidden h-6 flex items-center opacity-50">
              <div className="scrolling-presets flex gap-6">
                {[...SAMPLE_PROMPTS, ...SAMPLE_PROMPTS].map((p, idx) => (
                  <button key={idx} type="button" onClick={() => setPrompt(p)} className="text-[8px] font-black uppercase tracking-widest bg-white/5 hover:bg-white/10 text-slate-400 py-1 px-4 rounded-full border border-white/5 transition-all whitespace-nowrap">
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {editState.error && (
            <div className="text-center p-3 md:p-4 bg-rose-500/5 border border-rose-500/20 text-rose-500 rounded-2xl animate-in fade-in zoom-in-95">
              <span className="text-[10px] md:text-xs font-black tracking-widest uppercase">{editState.error}</span>
            </div>
          )}
        </div>
      </main>

      {history.length > 0 && (
        <section className="mt-12 md:mt-16 w-full animate-in fade-in slide-in-from-bottom-12 duration-1000 px-2">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 mb-6">
            <div className="flex items-center gap-4">
               <Icons.History className="w-5 h-5 text-slate-700" />
               <h2 className="text-[11px] font-black uppercase tracking-[0.5em] text-slate-500 whitespace-nowrap">Vision History</h2>
            </div>
            <div className="flex-1 h-px bg-white/5 hidden md:block"></div>
            <button onClick={clearAllHistory} className="text-[9px] font-black uppercase tracking-widest text-slate-600 hover:text-rose-500 transition-all text-left md:text-right">
              Clear History Cache
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
            {history.map((item) => (
              <div 
                key={item.id} 
                className={`group/item relative p-3 rounded-[2rem] border transition-all duration-500 cursor-pointer ${
                  currentImage === item.url ? 'bg-white/5 border-white/20' : 'bg-[#030303] border-white/5 hover:border-white/10'
                }`}
                onClick={() => { setCurrentImage(item.url); setOriginalImage(item.url); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              >
                <div className="aspect-square w-full rounded-[1.5rem] overflow-hidden bg-black/80 border border-white/5 mb-3 relative">
                  <img src={item.url} alt="History View" className="w-full h-full object-contain" />
                  <button onClick={(e) => deleteHistoryItem(item.id, e)} className="absolute top-2 right-2 p-2 bg-black/60 backdrop-blur-xl border border-white/10 text-slate-400 hover:text-rose-500 rounded-xl md:opacity-0 md:group-hover/item:opacity-100 transition-all"><Icons.Trash className="w-3.5 h-3.5" /></button>
                </div>
                <p className="text-[8px] md:text-[9px] font-bold text-slate-500 italic line-clamp-1 px-1">"{item.prompt}"</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <footer className="mt-auto pt-8 pb-4 border-t border-white/5 flex flex-col md:flex-row justify-between items-center w-full gap-4">
        <div className="text-[9px] md:text-[11px] font-black text-white uppercase tracking-[0.4em]">BabalVision AI ARCHITECTURE</div>
        <div className="flex items-center gap-2">
          <span className="uppercase text-[8px] md:text-[9px] font-bold tracking-widest text-white">Powered By</span> 
          <span className="font-black text-red-600 drop-shadow-[0_0_12px_rgba(220,38,38,1)]">Balram Pathak Official</span>
        </div>
      </footer>

      {isPreviewOpen && currentImage && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center lightbox-backdrop animate-in fade-in" onClick={() => setIsPreviewOpen(false)}>
          <div className="relative w-[96vw] h-[92vh] bg-black rounded-[2.5rem] md:rounded-[4rem] border border-white/10 overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="absolute top-6 right-6 md:top-12 md:right-12 flex gap-3 md:gap-6 z-20">
               <button onClick={() => downloadImage(currentImage)} className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-black/60 hover:bg-black/90 backdrop-blur-3xl border border-white/10 flex items-center justify-center text-white transition-all"><Icons.Download className="w-6 h-6" /></button>
               <button onClick={() => setIsPreviewOpen(false)} className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-black/60 hover:bg-black/90 backdrop-blur-3xl border border-white/10 flex items-center justify-center text-white transition-all"><Icons.X className="w-6 h-6" /></button>
            </div>
            <div className="w-full h-full flex items-center justify-center bg-black">
               <img src={currentImage} alt="Expanded Vision" className="w-full h-full object-contain" onClick={() => setIsPreviewOpen(false)} />
            </div>
          </div>
        </div>
      )}

      <input type="file" ref={fileInputRef} onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onloadend = async () => {
            const res = reader.result as string;
            const watermarked = await applyWatermark(res);
            setOriginalImage(watermarked);
            setCurrentImage(watermarked);
            setEditState({ isEditing: false, error: null, progressMessage: '' });
          };
          reader.readAsDataURL(file);
        }
      }} accept="image/jpeg,image/png,image/webp" className="hidden" />
    </div>
  );
};

export default App;